import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Input } from './ui/input';
import { Switch } from './ui/switch';
import OnOffToggle from './OnOffToggle';
import { Label } from './ui/label';
import ActionButton from './ui/action-button';
import { Shield, Eye, FileText, CheckCircle, Loader2, Download } from 'lucide-react';
import { toast } from 'sonner';
import api from '../utils/api';
import DashboardPageLayout from './DashboardPageLayout';
import FormSection from './FormSection';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';

const WaiversTabClean = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  const [waiversEnabled, setWaiversEnabled] = useState(false);
  const [waiverText, setWaiverText] = useState('');
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [signedWaivers, setSignedWaivers] = useState([]);
  const [selectedWaiver, setSelectedWaiver] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const waiversPerPage = 10;
  
  // Sorting
  const [sortBy, setSortBy] = useState('recent'); // 'recent' or 'oldest'

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const orgId = localStorage.getItem('organizationId');
      
      // Fetch organization data
      const orgResponse = await api.get(`/organizations/${orgId}`);
      const orgData = orgResponse.data;
      
      setWaiversEnabled(orgData.waiversEnabled || false);
      setWaiverText(orgData.customWaiverText || '');
      
      // Fetch signed waivers from trips with waiverSignatures
      const tripsResponse = await api.get(`/bookings`);
      const allBookings = tripsResponse.data || [];
      
      // Extract all waiver signatures from trips
      const waivers = [];
      allBookings.forEach(trip => {
        if (trip.waiverSignatures && trip.waiverSignatures.length > 0) {
          trip.waiverSignatures.forEach(sig => {
            waivers.push({
              ...sig,
              trip: {
                id: trip.id,
                date: trip.date,
                startTime: trip.startTime,
                confirmationCode: trip.confirmationCode
              }
            });
          });
        }
      });
      
      setSignedWaivers(waivers);
      
    } catch (error) {
      console.error('Error fetching waiver data:', error);
      toast.error('Failed to load waiver settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (waiversEnabled && waiverText.length < 100) {
      toast.error('Waiver text must be at least 100 characters');
      return;
    }

    setSaving(true);
    try {
      const orgId = localStorage.getItem('organizationId');
      await api.put(`/organizations/${orgId}`, {
        waiversEnabled: waiversEnabled,
        customWaiverText: waiverText
      });

      toast.success('Waiver settings saved successfully!');
    } catch (error) {
      console.error('Error saving waiver settings:', error);
      toast.error('Failed to save waiver settings');
    } finally {
      setSaving(false);
    }
  };

  const viewWaiverDetails = async (waiver) => {
    setSelectedWaiver(waiver);
    setShowDetailModal(true);
    
    // Log activity: Viewing sensitive waiver data
    try {
      await api.post('/activity-log/log-waiver-access', {
        waiverSignatureName: waiver.signatureName,
        tripId: waiver.trip?.id,
        accessedBy: localStorage.getItem('userData') ? JSON.parse(localStorage.getItem('userData')).username : 'Unknown',
        accessReason: 'Admin viewing waiver details'
      });
    } catch (error) {
      console.error('Error logging waiver access:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <DashboardPageLayout
      title="Waivers"
      subtitle="Configure digital waivers for customer trips"
      actions={
        <ActionButton
          onClick={handleSave}
          loading={saving}
          disabled={waiversEnabled && waiverText.length < 100}
          icon={CheckCircle}
        >
          Save Settings
        </ActionButton>
      }
    >
      <div className="space-y-6">
        {/* STEP 1: Enable & Configure Waiver */}
        <FormSection
          stepNumber={1}
          title="Waiver Configuration"
          description="Enable waivers and write your custom waiver text"
          color="#3B82F6"
        >
          <div className="space-y-4">
            {/* Enable/Disable - Button Style */}
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-md border border-gray-200">
              <div>
                <Label className="text-sm sm:text-base font-semibold">Enable Waivers</Label>
                <p className="text-xs sm:text-sm text-gray-600 mt-1">
                  Require customers to sign waivers
                </p>
              </div>
              <OnOffToggle 
                value={waiversEnabled} 
                onChange={setWaiversEnabled}
              />
            </div>

            {/* Waiver Text Editor */}
            {waiversEnabled && (
              <div>
                <Label className="text-base font-semibold mb-2 block">
                  Custom Waiver Text * (Minimum 100 characters)
                </Label>
                
                <Textarea
                  value={waiverText}
                  onChange={(e) => setWaiverText(e.target.value)}
                  placeholder="Enter your waiver text here... (must be at least 100 characters)"
                  className="min-h-[200px] text-base"
                />
                <p className="text-base text-gray-500 mt-2">
                  {waiverText.length} characters
                  {waiverText.length < 100 && waiverText.length > 0 && (
                    <span className="text-red-600 ml-2">
                      ({100 - waiverText.length} more needed)
                    </span>
                  )}
                </p>
              </div>
            )}
          </div>
        </FormSection>

        {/* STEP 2: Preview Waiver */}
        {waiversEnabled && waiverText && (
          <FormSection
            stepNumber={2}
            title="Preview Waiver"
            description="See how customers will see your waiver text"
            color="#9333EA"
          >
            <div className="space-y-4">
              {/* Best Practice Notice */}
              <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
                <p className="text-base font-semibold text-blue-900 mb-1">💡 Best Practice: Physical Backup Waivers</p>
                <p className="text-base text-blue-800 leading-relaxed">
                  While digital waivers are legally enforceable, we recommend keeping printed physical waivers on-site as backup. You can print this waiver and have customers sign and date it in person if digital systems fail (SMS verification, internet outages, etc.). The printed version includes signature and date fields at the bottom.
                </p>
              </div>
              
              <button
                onClick={() => setShowPreviewModal(true)}
                className="flex items-center gap-2 text-gray-700 opacity-80 hover:opacity-100 font-medium text-lg px-6 py-3 border border-gray-300 rounded-md hover:bg-gray-50 transition-all"
              >
                <Eye className="w-5 h-5" />
                Open Preview
              </button>
            </div>
          </FormSection>
        )}

        {/* STEP 3: Signed Waivers List */}
        <FormSection
          stepNumber={3}
          title={`Signed Waivers (${signedWaivers.length})`}
          description="View all customer waiver signatures with device verification"
          color="#10B981"
        >
          {signedWaivers.length === 0 ? (
            <div className="text-center py-8 bg-gray-50 rounded-lg border border-gray-200">
              <Shield className="w-12 h-12 mx-auto text-gray-300 mb-2" />
              <p className="text-gray-600">No signed waivers yet</p>
              <p className="text-xs text-gray-500 mt-1">Waivers will appear here after customers sign</p>
            </div>
          ) : (
            <>
              {/* Sort Filter */}
              <div className="flex items-center justify-between mb-4">
                <p className="text-sm text-gray-600">
                  Showing {((currentPage - 1) * waiversPerPage) + 1}-{Math.min(currentPage * waiversPerPage, signedWaivers.length)} of {signedWaivers.length}
                </p>
                <select
                  value={sortBy}
                  onChange={(e) => {
                    setSortBy(e.target.value);
                    setCurrentPage(1); // Reset to page 1 when sorting changes
                  }}
                  className="text-sm border border-gray-300 rounded px-3 py-1"
                >
                  <option value="recent">Most Recent First</option>
                  <option value="oldest">Oldest First</option>
                </select>
              </div>

              {/* Waivers List */}
              <div className="space-y-3">
                {signedWaivers
                  .sort((a, b) => {
                    const dateA = new Date(a.signedAt).getTime();
                    const dateB = new Date(b.signedAt).getTime();
                    return sortBy === 'recent' ? dateB - dateA : dateA - dateB;
                  })
                  .slice((currentPage - 1) * waiversPerPage, currentPage * waiversPerPage)
                  .map((waiver, idx) => (
                    <div 
                      key={idx}
                      className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md hover:border-blue-300 transition-all cursor-pointer"
                      onClick={() => viewWaiverDetails(waiver)}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <p className="font-semibold text-gray-900">{waiver.signatureName}</p>
                          <p className="text-sm text-gray-600 mt-1">
                            Booking: {waiver.trip?.date} at {waiver.trip?.startTime}
                          </p>
                          <p className="text-xs text-gray-500 mt-1">
                            Signed: {new Date(waiver.signedAt).toLocaleString()}
                          </p>
                        </div>
                        <div className="flex flex-col items-end gap-2">
                          <div className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded font-semibold">
                            Verified
                          </div>
                          <p className="text-xs text-gray-500">Click for details</p>
                        </div>
                      </div>
                    </div>
                  ))}
              </div>
              
              {/* Pagination */}
              {signedWaivers.length > waiversPerPage && (
                <div className="flex items-center justify-between mt-4 pt-4 border-t">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                  >
                    Previous
                  </Button>
                  <span className="text-sm text-gray-600">
                    Page {currentPage} of {Math.ceil(signedWaivers.length / waiversPerPage)}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(p => Math.min(Math.ceil(signedWaivers.length / waiversPerPage), p + 1))}
                    disabled={currentPage >= Math.ceil(signedWaivers.length / waiversPerPage)}
                  >
                    Next
                  </Button>
                </div>
              )}
            </>
          )}
        </FormSection>
      </div>

      {/* Waiver Detail Modal - ALL SENSITIVE DATA */}
      {selectedWaiver && (
        <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-blue-600" />
                Waiver Signature Details
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-6 mt-4">
              {/* Basic Info */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-blue-900 uppercase font-semibold mb-1">Signer Name</p>
                    <p className="font-bold text-gray-900">{selectedWaiver.signatureName}</p>
                  </div>
                  <div>
                    <p className="text-xs text-blue-900 uppercase font-semibold mb-1">Signed At</p>
                    <p className="font-semibold">{new Date(selectedWaiver.signedAt).toLocaleString()}</p>
                  </div>
                  <div>
                    <p className="text-xs text-blue-900 uppercase font-semibold mb-1">Booking Date</p>
                    <p className="font-semibold">
                      {selectedWaiver.trip?.date} at {selectedWaiver.trip?.startTime}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-blue-900 uppercase font-semibold mb-1">Booking ID</p>
                    <p className="font-mono font-semibold">{selectedWaiver.trip?.confirmationCode || selectedWaiver.trip?.id?.substring(0, 8) || 'N/A'}</p>
                  </div>
                </div>
              </div>

              {/* VERIFICATION METHOD */}
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <h4 className="text-sm font-bold text-green-900 mb-3 uppercase">Verification Method</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-green-900 uppercase font-semibold mb-1">Method</p>
                    <p className="font-bold text-gray-900">
                      {selectedWaiver.verificationMethod === 'sms' ? 'SMS Verified' : 'ID Upload Required'}
                    </p>
                  </div>
                  {selectedWaiver.verificationMethod === 'sms' && selectedWaiver.phoneNumber && (
                    <div>
                      <p className="text-xs text-green-900 uppercase font-semibold mb-1">Verified Phone Number</p>
                      <p className="font-mono font-bold text-gray-900">{selectedWaiver.phoneNumber}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* DEVICE FINGERPRINT - TERMINAL STYLE */}
              <div className="bg-gray-900 border border-gray-700 rounded-lg p-4">
                <h4 className="text-sm font-bold text-green-400 mb-3 uppercase font-mono">DEVICE FINGERPRINT</h4>
                <div className="space-y-2 font-mono text-xs">
                  <div className="flex">
                    <span className="text-green-400 w-32">IP_ADDRESS:</span>
                    <span className="text-white">{selectedWaiver.ipAddress || 'Not captured'}</span>
                  </div>
                  <div className="flex">
                    <span className="text-green-400 w-32">PLATFORM:</span>
                    <span className="text-white">{selectedWaiver.deviceInfo?.platform || 'Unknown'}</span>
                  </div>
                  <div className="flex">
                    <span className="text-green-400 w-32">BROWSER:</span>
                    <span className="text-white">{selectedWaiver.deviceInfo?.browser || 'Unknown'}</span>
                  </div>
                  <div className="flex">
                    <span className="text-green-400 w-32">SCREEN_SIZE:</span>
                    <span className="text-white">{selectedWaiver.deviceInfo?.screenResolution || 'Unknown'}</span>
                  </div>
                  <div className="flex">
                    <span className="text-green-400 w-32">LANGUAGE:</span>
                    <span className="text-white">{selectedWaiver.deviceInfo?.language || 'Unknown'}</span>
                  </div>
                </div>
              </div>

              {/* USER AGENT - TERMINAL STYLE */}
              <div className="bg-gray-900 border border-gray-700 rounded-lg p-4">
                <h4 className="text-sm font-bold text-green-400 mb-2 uppercase font-mono">USER_AGENT</h4>
                <p className="font-mono text-xs text-green-300 break-all leading-relaxed">
                  {selectedWaiver.deviceInfo?.userAgent || 'Not captured'}
                </p>
              </div>

              {/* Signature Image */}
              {selectedWaiver.signatureDataUrl && (
                <div>
                  <h4 className="text-sm font-bold text-gray-900 mb-3 uppercase">Digital Signature</h4>
                  <div className="bg-white border-2 border-gray-300 rounded-lg p-4">
                    <img 
                      src={selectedWaiver.signatureDataUrl} 
                      alt="Signature" 
                      className="max-w-full h-auto"
                    />
                  </div>
                </div>
              )}

              {/* Legal Notice */}
              <div className="bg-yellow-50 border border-yellow-300 rounded-lg p-4">
                <p className="text-xs text-yellow-900">
                  <strong>⚠️ Legal Notice:</strong> This waiver signature was captured with device verification and is legally binding. 
                  All data including IP address, device fingerprint, and signature are stored securely for legal compliance. 
                  Access to this information is logged in the Activity Log.
                </p>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Waiver Preview Modal */}
      {showPreviewModal && (
        <Dialog open={showPreviewModal} onOpenChange={setShowPreviewModal}>
          <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Waiver Preview</DialogTitle>
            </DialogHeader>
            <div className="mt-4">
              <style>
                {`
                  @media print {
                    body { margin: 0.5in; }
                    #waiver-print-content { 
                      max-width: 100%; 
                      page-break-inside: avoid;
                    }
                    #waiver-print-content h3 { 
                      page-break-after: avoid; 
                    }
                    #waiver-print-content .signature-section {
                      page-break-before: auto;
                      margin-top: 1in;
                    }
                  }
                `}
              </style>
              <div className="bg-white border border-gray-200 rounded-lg p-8" id="waiver-print-content">
                <h3 className="font-bold text-xl mb-6 text-center">Waiver Agreement</h3>
                <div className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap mb-8" style={{ wordWrap: 'break-word', overflowWrap: 'break-word' }}>
                  {waiverText}
                </div>
                
                {/* Signature Fields for Physical Print */}
                <div className="border-t-2 border-gray-300 pt-8 mt-8 signature-section">
                  <p className="text-xs text-gray-600 mb-6">
                    By signing below, I acknowledge that I have read, understood, and agree to the terms of this waiver.
                  </p>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div>
                      <p className="text-xs font-semibold text-gray-700 mb-2">PRINT NAME</p>
                      <div className="border-b-2 border-gray-400 h-10"></div>
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-gray-700 mb-2">SIGNATURE</p>
                      <div className="border-b-2 border-gray-400 h-10"></div>
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-gray-700 mb-2">DATE</p>
                      <div className="border-b-2 border-gray-400 h-10"></div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="mt-6 flex gap-3 justify-end print:hidden">
                <Button variant="outline" onClick={() => setShowPreviewModal(false)}>
                  Close
                </Button>
                <Button onClick={() => window.print()} className="bg-blue-600 hover:bg-blue-700">
                  <Download className="w-4 h-4 mr-2" />
                  Print Waiver
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </DashboardPageLayout>
  );
};

export default WaiversTabClean;
